import React, { useCallback, useMemo, useState, useContext, useEffect, useRef } from 'react';
import {
  useTable,
  useSortBy,
  useExpanded,
  usePagination,
  useRowSelect,
  useColumnOrder,
  TableInstance,
  HeaderProps,
  CellProps,
  Column,
  ColumnInstance,
  TableToggleAllRowsSelectedProps,
  TableToggleRowsSelectedProps
} from 'react-table';
import { useTableColumnPrefs } from './hooks/useTableColumnPrefs';
import Grid from '@mui/material/Grid';
import TableContainer from '@mui/material/TableContainer';
import MaterialTable from '@mui/material/Table';
import Checkbox from '@mui/material/Checkbox';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DeleteIcon from '@mui/icons-material/Delete';

import { Alert, ErrorAlert, Button, TextView } from '../../atoms';

import StyledTable from './table.style';
import {
  TableBody,
  TableCell,
  TableHeader,
  TablePagination,
  ColumnVisibilityControl
} from './partials';

type VisibilityColumn = ColumnInstance<TableRow> & { canHide?: boolean; defaultHidden?: boolean };
import { TableContext } from '@particles';

export interface TableProps {
  /** Table title */
  title?: string;
  /** Table name, used for persisting user preferences */
  name: TableType;
  /** Table columns */
  columns: TableColumn[];
  /** Table data */
  data: TableRow[];
  /** Table pagination data, pagination will be disabled if not provided */
  pagination?: Pagination;
  /** If true, the API request is still awaiting results */
  isLoading: boolean;
  /** The error returned by the API, if there is one */
  error?: Error | null;
  /** If true, the rows will be selectable */
  selectable?: boolean;
  /** Callback function called when selected rows change */
  onSelectionChange?: (selectedRows: TableRow[]) => void;
  /** Callback function called when bulk delete is triggered. Receives array of selected rows. */
  onBulkDelete?: (selectedRows: TableRow[]) => Promise<void> | void;
  /** Label for the bulk delete button (default: "Delete Selected") */
  bulkDeleteLabel?: string;
  /** Label for the bulk delete dialog title (default: "Delete Multiple Items") */
  bulkDeleteDialogTitle?: string;
  /** Function to get display text for a row in the delete confirmation dialog */
  getRowDisplayText?: (row: TableRow) => string;
  /** Removes the ability to change page size */
  disablePageSize?: boolean;
  /** If true, switches from normal row style to card style */
  cards?: boolean;
  /** If true, switches from normal row style to card style */
  noResultsMessage?: string;
  /** If true, the table persists per-user column order / visibility / sort
   *  to the server (UserTablePreferences). Default false. */
  persistColumnPrefs?: boolean;
}

export const Table: React.FC<TableProps> = ({
  title,
  name,
  columns,
  data,
  pagination,
  isLoading,
  error,
  selectable,
  onSelectionChange,
  onBulkDelete,
  bulkDeleteLabel = 'Delete Selected',
  bulkDeleteDialogTitle = 'Delete Multiple Items',
  getRowDisplayText,
  disablePageSize,
  cards = false,
  noResultsMessage,
  persistColumnPrefs = false
}: TableProps) => {
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    dispatch,
    state: {
      unifiedUsersPagination,
      coreAccountsPagination,
      coreContactsPagination,
      coreOfficesPagination,
      coreUsersPagination,
      teamPagination,
      billingPagination,
      accountPerformancePagination,
      billingAccountPagination,
      openAiPagination,
      twilioPagination,
      srtPagination,
      companyTransactionsLogsPagination,
      apiKeys,
      zazuAuditLogsPagination,
      artifactsPagination,
      jobsPagination
    }
  } = useContext(TableContext);

  // Map table name → TableContext state key. Used by perPage hydration and
  // the persist-on-change callback below. Keep in sync with TableState in
  // generalDeclarations.d.ts.
  const paginationKey = useMemo((): TableSetReducerAction['key'] | null => {
    switch (name) {
      case 'unified-users':
        return 'unifiedUsersPagination';
      case 'core_accounts':
        return 'coreAccountsPagination';
      case 'core_users':
        return 'coreUsersPagination';
      case 'core_contacts':
        return 'coreContactsPagination';
      case 'core_offices':
        return 'coreOfficesPagination';
      case 'team':
        return 'teamPagination';
      case 'billing':
        return 'billingPagination';
      case 'account_performance':
        return 'accountPerformancePagination';
      case 'billing-accounts':
        return 'billingAccountPagination';
      case 'open-ai':
        return 'openAiPagination';
      case 'twilio':
        return 'twilioPagination';
      case 'srt':
        return 'srtPagination';
      case 'company-transactions-logs':
        return 'companyTransactionsLogsPagination';
      case 'api-keys':
        return 'apiKeys';
      case 'zazu-audit-logs':
        return 'zazuAuditLogsPagination';
      case 'artifacts':
        return 'artifactsPagination';
      case 'jobs':
        return 'jobsPagination';
      default:
        return null;
    }
  }, [name]);

  let pageSize = 10;

  switch (name) {
    case 'unified-users':
      pageSize = unifiedUsersPagination.perPage;
      break;
    case 'core_accounts':
      pageSize = coreAccountsPagination.perPage;
      break;
    case 'core_users':
      pageSize = coreUsersPagination.perPage;
      break;
    case 'core_contacts':
      pageSize = coreContactsPagination.perPage;
      break;
    case 'core_offices':
      pageSize = coreOfficesPagination.perPage;
      break;
    case 'team':
      pageSize = teamPagination.perPage;
      break;
    case 'billing':
      pageSize = billingPagination.perPage;
      break;
    case 'account_performance':
      pageSize = accountPerformancePagination.perPage;
      break;
    case 'billing-accounts':
      pageSize = billingAccountPagination.perPage;
      break;
    case 'open-ai':
      pageSize = openAiPagination.perPage;
      break;
    case 'twilio':
      pageSize = twilioPagination.perPage;
      break;
    case 'srt':
      pageSize = srtPagination.perPage;
      break;
    case 'company-transactions-logs':
      pageSize = companyTransactionsLogsPagination.perPage;
      break;
    case 'api-keys':
      pageSize = apiKeys.perPage;
      break;
    case 'zazu-audit-logs':
      pageSize = zazuAuditLogsPagination.perPage;
      break;
    case 'artifacts':
      pageSize = artifactsPagination.perPage;
      break;
    case 'jobs':
      pageSize = jobsPagination.perPage;
      break;
  }

  // Adds the page size prop or query value to the list of available options
  const rowsPerPageOptions = [...new Set([5, 10, 25, 50, 100])].sort((a, b) => a - b);

  // Fetch persisted prefs BEFORE building the table instance so the initial
  // column order matches the user's saved layout — avoids the timing bug where
  // setColumnOrder-after-mount fails to override react-table's initial render.
  const {
    prefs,
    ready: prefsReady,
    mutate: mutatePrefs,
    reset: resetPrefs
  } = useTableColumnPrefs(name, persistColumnPrefs);

  // Loops through the provided columns and sets the custom table cell
  const customCellColumns = useMemo(
    () => columns.map((col) => ({ ...col, Cell: TableCell })),
    [columns]
  );

  // Pre-sort columns based on the saved order. When prefs has a columnOrder,
  // we permute the columns array *before* react-table sees it — that way the
  // initial render uses the right order without depending on setColumnOrder,
  // which has timing issues in react-table v7 when called from a post-mount
  // effect.
  const orderedCellColumns = useMemo(() => {
    if (!persistColumnPrefs || !prefs?.columnOrder?.length) return customCellColumns;
    const byAccessor = new Map(customCellColumns.map((col) => [col.accessor, col]));
    const ordered: typeof customCellColumns = [];
    for (const accessor of prefs.columnOrder) {
      const col = byAccessor.get(accessor);
      if (col) {
        ordered.push(col);
        byAccessor.delete(accessor);
      }
    }
    // Append any columns that weren't in the saved order (e.g. newly added).
    ordered.push(...byAccessor.values());
    return ordered;
  }, [customCellColumns, persistColumnPrefs, prefs]);

  // Pre-compute hidden columns for initialState. Re-init of react-table reads
  // initialState fresh, so this also covers the "drag reorders, visibility
  // shouldn't reset" case.
  const initialHiddenColumns = useMemo(() => {
    if (persistColumnPrefs && prefs?.hiddenColumns) return prefs.hiddenColumns;
    return customCellColumns
      .filter((c) => (c as TableColumn).defaultHidden)
      .map((c) => c.accessor);
  }, [customCellColumns, persistColumnPrefs, prefs]);

  // Creates table instance.
  // The key on the wrapping useMemo isn't possible here, so we instead force
  // react-table to re-initialize when prefs first arrive by including
  // `prefsReady` in a memo key downstream.
  const tableInstance: TableInstance<TableRow> = useTable(
    {
      columns: orderedCellColumns,
      data,
      initialState: { pageSize: pageSize, hiddenColumns: initialHiddenColumns }
    },
    useColumnOrder,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,

    (hooks) => {
      hooks.visibleColumns.push((hookColumns) => {
        const arr: Column<TableRow>[] = hookColumns;

        if (selectable) {
          arr.unshift({
            id: `_selector`,
            Header: function RenderCheckbox({
              getToggleAllRowsSelectedProps
            }: HeaderProps<TableRow>) {
              return <CellCheckbox {...getToggleAllRowsSelectedProps()} />;
            },
            Cell: function RenderCheckbox({ row }: CellProps<TableRow>) {
              return <CellCheckbox {...row.getToggleRowSelectedProps()} />;
            }
          });
        }
        return arr;
      });
    }
  );
  const {
    columns: finalColumns,
    getTableProps,
    allColumns,
    visibleColumns: liveVisibleColumns,
    selectedFlatRows,
    setColumnOrder
  } = tableInstance;

  // Build a full TablePrefs snapshot from whatever changed. Hidden columns
  // come from allColumns (which knows visible state for every column); column
  // order is taken from the LIVE visible columns (which reflects react-table's
  // useColumnOrder state — i.e. the user's latest drag order), not allColumns
  // (which stays in original definition order). Falling back to allColumns
  // would silently overwrite a saved drag order on the next visibility toggle.
  const snapshotPrefs = useCallback(
    (overrides?: Partial<TablePrefs>): TablePrefs => {
      const liveOrder = liveVisibleColumns
        .filter((col) => col.id !== '_selector')
        .map((col) => String(col.id)) as string[];
      const hiddenFromAll = (allColumns
        .filter((col) => col.id !== '_selector' && col.isVisible === false)
        .map((col) => String(col.id)) as string[]);
      // For order: prefer the explicit override; else prefer the live drag order;
      // else fall back to whatever prefs already had.
      const order =
        overrides?.columnOrder ??
        (liveOrder.length > 0 ? liveOrder : prefs?.columnOrder ?? []);
      const hidden = overrides?.hiddenColumns ?? hiddenFromAll;
      const sort = overrides?.sort !== undefined ? overrides.sort : prefs?.sort ?? null;
      const perPage =
        overrides?.perPage !== undefined ? overrides.perPage : prefs?.perPage;
      const snapshot: TablePrefs = { columnOrder: order, hiddenColumns: hidden, sort };
      if (typeof perPage === 'number') snapshot.perPage = perPage;
      return snapshot;
    },
    [allColumns, liveVisibleColumns, prefs]
  );

  const handleOrderChange = useCallback(
    (next: string[]) => {
      mutatePrefs(snapshotPrefs({ columnOrder: next }));
    },
    [mutatePrefs, snapshotPrefs]
  );

  const handleHiddenChange = useCallback(
    (hidden: string[]) => {
      mutatePrefs(snapshotPrefs({ hiddenColumns: hidden }));
    },
    [mutatePrefs, snapshotPrefs]
  );

  const handleReset = useCallback(async () => {
    await resetPrefs();
    // Clear react-table's internal columnOrder so re-renders fall back to the
    // natural array order (set by orderedCellColumns when prefs is null).
    setColumnOrder([]);
  }, [resetPrefs, setColumnOrder]);

  const handleSortChange = useCallback(
    (sort: TablePrefsSort | null) => {
      mutatePrefs(snapshotPrefs({ sort }));
    },
    [mutatePrefs, snapshotPrefs]
  );

  const selectedRows = useMemo(() => selectedFlatRows.map((row) => row.original), [selectedFlatRows]);

  // Notify parent component when selection changes
  useEffect(() => {
    if (selectable && onSelectionChange) {
      onSelectionChange(selectedRows);
    }
  }, [selectedRows, selectable, onSelectionChange]);

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (!onBulkDelete || selectedRows.length === 0) return;

    setIsDeleting(true);
    try {
      await onBulkDelete(selectedRows);
      setBulkDeleteDialogOpen(false);
      // Clear selection after successful delete
      selectedFlatRows.forEach((row) => {
        row.toggleRowSelected(false);
      });
    } catch (error) {
      // Error handling is done by the parent component
      console.error('Bulk delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const defaultGetRowDisplayText = (row: TableRow): string => {
    // Try to get a meaningful display text from common row properties
    if (typeof row.title === 'string') return row.title;
    if (typeof row.name === 'string') return row.name;
    if (typeof row.id === 'string') return row.id;
    return 'Item';
  };
  const defaultsAppliedRef = useRef(false);
  const perPageHydratedRef = useRef(false);

  // Hydration effect. Column ORDER is handled by `orderedCellColumns` above
  // (pre-sort before useTable), so this only needs to deal with VISIBILITY.
  // Runs once when prefs (or defaults) are ready.
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    if (persistColumnPrefs && !prefsReady) return;

    const visibilityColumns = allColumns as VisibilityColumn[];

    if (persistColumnPrefs && prefs) {
      const hiddenSet = new Set(prefs.hiddenColumns ?? []);
      visibilityColumns.forEach((column) => {
        if (column.id === '_selector' || column.canHide === false) return;
        const shouldHide = hiddenSet.has(String(column.id));
        if (column.isVisible !== !shouldHide) column.toggleHidden(shouldHide);
      });
    } else {
      visibilityColumns.forEach((column) => {
        if (column.id === '_selector' || column.canHide === false) return;
        const shouldHide = Boolean(column.defaultHidden);
        if (column.isVisible === !shouldHide) return;
        column.toggleHidden(shouldHide);
      });
    }
    defaultsAppliedRef.current = true;
  }, [allColumns, persistColumnPrefs, prefs, prefsReady]);

  // perPage hydration. When the user has a saved page size and it differs
  // from the current default in TableContext, dispatch the update so the
  // parent page's query refetches at the saved size. Runs once.
  useEffect(() => {
    if (perPageHydratedRef.current) return;
    if (!persistColumnPrefs || !prefsReady) return;
    if (!paginationKey || !prefs?.perPage) {
      perPageHydratedRef.current = true;
      return;
    }
    if (prefs.perPage !== pageSize) {
      dispatch({
        type: 'set',
        key: paginationKey,
        value: { page: 1, perPage: prefs.perPage }
      });
    }
    perPageHydratedRef.current = true;
  }, [persistColumnPrefs, prefsReady, prefs, paginationKey, pageSize, dispatch]);

  // Persist perPage when the user changes "rows per page" via the pagination
  // footer. The TableContext dispatch already happens in TablePagination —
  // this callback is the one that writes the choice to the server.
  const handlePerPageChange = useCallback(
    (next: number) => {
      if (!persistColumnPrefs) return;
      mutatePrefs(snapshotPrefs({ perPage: next } as Partial<TablePrefs>));
    },
    [persistColumnPrefs, mutatePrefs, snapshotPrefs]
  );

  const minTableWidth =
    finalColumns && finalColumns.length > 0 ? `${Math.max(finalColumns.length * 150, 640)}px` : `100%`;

  const showBulkActions = selectable && onBulkDelete && selectedRows.length > 0;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Bulk Actions Bar */}
      {showBulkActions && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <TextView variant="body1" $fontWeight={600} sx={{ color: 'white' }}>
            {selectedRows.length} item(s) selected
          </TextView>
          <Button
            variant="contained"
            startIcon={<DeleteIcon htmlColor="white" />}
            onClick={handleBulkDelete}
            sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
            {bulkDeleteLabel} ({selectedRows.length})
          </Button>
        </Box>
      )}

      <StyledTable $headerHeight={headerHeight}>
        <Box className="table-toolbar">
          {title && (
            <TextView variant="h6" $fontWeight={600} sx={{ flex: 1 }}>
              {title}
            </TextView>
          )}
          <ColumnVisibilityControl
            tableInstance={tableInstance}
            persistColumnPrefs={persistColumnPrefs}
            onHiddenChange={persistColumnPrefs ? handleHiddenChange : undefined}
            onReset={persistColumnPrefs ? handleReset : undefined}
          />
        </Box>
        <TableContainer className={cards ? 'table-cards' : 'table-scroll-container'}>
          {cards && <div className="header-background" />}
          <MaterialTable
            stickyHeader
            sx={{
              borderCollapse: 'separate',
              borderSpacing: 0,
              width: cards ? '100%' : 'max-content',
              minWidth: cards ? '100%' : minTableWidth
            }}
            {...getTableProps()}>
            <TableHeader
              name={name}
              tableInstance={tableInstance}
              setHeaderHeight={setHeaderHeight}
              persistColumnPrefs={persistColumnPrefs}
              persistedSort={prefs?.sort ?? null}
              onOrderChange={persistColumnPrefs ? handleOrderChange : undefined}
              onSortChange={persistColumnPrefs ? handleSortChange : undefined}
            />
            {!!error && <TableError error={error} colSpan={finalColumns.length} />}
            {!error && (
              <TableBody pageSize={pageSize} tableInstance={tableInstance} isLoading={isLoading} />
            )}
            {!data.length && !error && !isLoading && (
              <TableNoResults colSpan={finalColumns.length} noResultsMessage={noResultsMessage} />
            )}
          </MaterialTable>
        </TableContainer>
      </StyledTable>
      <Grid container justifyContent="flex-end">
        {pagination && (
          <TablePagination
            name={name}
            tableInstance={tableInstance}
            rowsPerPageOptions={rowsPerPageOptions}
            pagination={pagination}
            disablePageSize={disablePageSize}
            onPerPageChange={persistColumnPrefs ? handlePerPageChange : undefined}
          />
        )}
      </Grid>

      {/* Bulk Delete Confirmation Dialog */}
      {onBulkDelete && (
        <Dialog open={bulkDeleteDialogOpen} onClose={() => !isDeleting && setBulkDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DeleteIcon sx={{ mr: 1, color: 'error.main' }} />
              <TextView variant="h6" $fontWeight={600}>
                {bulkDeleteDialogTitle}
              </TextView>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <TextView variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to delete {selectedRows.length} item(s)?
            </TextView>
            {selectedRows.length > 0 && (
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, maxHeight: '200px', overflowY: 'auto' }}>
                {selectedRows.slice(0, 10).map((row, index) => {
                  const displayText = getRowDisplayText ? getRowDisplayText(row) : defaultGetRowDisplayText(row);
                  return (
                    <Box key={index} sx={{ mb: 1 }}>
                      <TextView $fontWeight={600} variant="body2">
                        {displayText}
                      </TextView>
                    </Box>
                  );
                })}
                {selectedRows.length > 10 && (
                  <TextView variant="body2" color="textSecondary">
                    ... and {selectedRows.length - 10} more
                  </TextView>
                )}
              </Box>
            )}
            <TextView variant="body2" color="error" sx={{ mt: 2 }}>
              This action cannot be undone.
            </TextView>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => setBulkDeleteDialogOpen(false)} 
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={confirmBulkDelete}
              loading={isDeleting}
              startIcon={<DeleteIcon htmlColor="white" />}
              sx={{ 
                bgcolor: 'error.main', 
                color: 'white',
                '&:hover': { bgcolor: 'error.dark' }
              }}>
              Delete {selectedRows.length} Item(s)
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

const CellCheckbox = (rowProps: TableToggleAllRowsSelectedProps | TableToggleRowsSelectedProps) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Checkbox
        {...rowProps}
        onClick={handleClick}
        color="primary"
        inputProps={{ 'aria-label': 'select row' }}
      />
    </Box>
  );
};

interface TableErrorProps {
  /** The error returned by the API, if there is one */
  error: Error;
  /** The number of columns in the table */
  colSpan: number;
}

const TableError: React.FC<TableErrorProps> = ({ error, colSpan }: TableErrorProps) => {
  const alertTitle = `There was an error loading the table data. If this problem persists, please contact the support team.`;

  return (
    <tbody>
      <tr>
        <td colSpan={colSpan}>
          <Box m={3}>
            <ErrorAlert variant="outlined" title={alertTitle} error={error} />
          </Box>
        </td>
      </tr>
    </tbody>
  );
};

interface TableNoResultsProps {
  /** The number of columns in the table */
  colSpan: number;
  /** If true, switches from normal row style to card style */
  noResultsMessage?: string;
}

const TableNoResults: React.FC<TableNoResultsProps> = ({
  colSpan,
  noResultsMessage
}: TableNoResultsProps) => {
  const message = noResultsMessage ?? `There were no results returned for this query`;

  return (
    <tbody>
      <tr>
        <td colSpan={colSpan}>
          <Box m={3}>
            <Alert severity="info" variant="outlined" title="No results" message={message} />
          </Box>
        </td>
      </tr>
    </tbody>
  );
};

export default Table;
